import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ListSkeleton } from '../components/ui/skeleton';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import {
  History, Trash2, ChevronRight, ChevronDown, Moon, Sun, Calculator, Package, Search, AlertTriangle, Undo2, CheckSquare, X, Zap, Lock
} from 'lucide-react';
import { format, isToday, isYesterday, parseISO, differenceInDays } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All groups default to expanded - no initial collapse
const getInitialCollapsedSections = () => {
  return new Set();
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, token } = useAuth();
  const { localHistory } = useGuest();
  
  const isGuest = !user;
  const GUEST_HISTORY_LIMIT = 5;
  
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  // Multi-select state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Undo state
  const [pendingDelete, setPendingDelete] = useState(null);
  const undoTimeoutRef = useRef(null);
  
  // Collapsed sections - will be initialized after first data load
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  // Helper to get date group key
  const getDateGroupKey = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMM d');
  };

  useEffect(() => {
    const fetchScans = async () => {
      // For guests, use local history limited to 5 items
      if (isGuest || !token) {
        const guestScans = localHistory.slice(0, GUEST_HISTORY_LIMIT);
        setScans(guestScans);
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/scans`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const fetchedScans = response.data;
        setScans(fetchedScans);
        
        // Initialize collapsed sections - collapse all except Today
        const sectionsToCollapse = new Set();
        fetchedScans.forEach(scan => {
          const dateKey = getDateGroupKey(scan.created_at);
          if (dateKey !== 'Today') {
            sectionsToCollapse.add(dateKey);
          }
        });
        setCollapsedSections(sectionsToCollapse);
      } catch (error) {
        console.error('Failed to fetch searches:', error);
      }
      setLoading(false);
    };
    fetchScans();
  }, [isGuest, localHistory, token]);

  // Cleanup undo timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  const deleteScan = async (id, e) => {
    e?.stopPropagation();
    
    // Find the scan to delete
    const scanToDelete = scans.find(s => s.id === id);
    if (!scanToDelete) return;
    
    // Remove from UI immediately
    setScans(prev => prev.filter(s => s.id !== id));
    
    // Clear any existing undo timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      // Execute pending delete immediately
      if (pendingDelete) {
        axios.delete(`${API}/scans/${pendingDelete.id}`).catch(() => {});
      }
    }
    
    // Set pending delete with undo option
    setPendingDelete({ id, scan: scanToDelete });
    
    // Auto-delete after 4 seconds
    undoTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.delete(`${API}/scans/${id}`);
      } catch (error) {
        console.error('Failed to delete:', error);
      }
      setPendingDelete(null);
    }, 4000);
  };

  const undoDelete = () => {
    if (!pendingDelete) return;
    
    // Clear timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }
    
    // Restore the scan
    setScans(prev => [...prev, pendingDelete.scan].sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    ));
    
    setPendingDelete(null);
    toast.success('Search restored');
  };

  const deleteAllScans = async () => {
    setDeletingAll(true);
    try {
      // Delete all scans
      await Promise.all(scans.map(scan => 
        axios.delete(`${API}/scans/${scan.id}`)
      ));
      setScans([]);
      toast.success('All searches deleted');
    } catch (error) {
      toast.error('Failed to delete some searches');
    }
    setDeletingAll(false);
    setShowDeleteAllDialog(false);
  };

  // Multi-select functions
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === scans.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(scans.map(s => s.id)));
    }
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const deleteSelectedScans = async () => {
    if (selectedIds.size === 0) return;
    
    const idsToDelete = Array.from(selectedIds);
    
    // Remove from UI
    setScans(prev => prev.filter(s => !selectedIds.has(s.id)));
    
    // Delete from server
    try {
      await Promise.all(idsToDelete.map(id => 
        axios.delete(`${API}/scans/${id}`)
      ));
      toast.success(`${idsToDelete.length} search${idsToDelete.length > 1 ? 'es' : ''} deleted`);
    } catch (error) {
      toast.error('Failed to delete some searches');
    }
    
    exitSelectMode();
  };

  // Group scans by date
  const groupedScans = useMemo(() => {
    const groups = {};
    
    scans.forEach(scan => {
      const date = parseISO(scan.created_at);
      const dateKey = format(date, 'yyyy-MM-dd');
      
      if (!groups[dateKey]) {
        groups[dateKey] = {
          date,
          dateKey,
          scans: []
        };
      }
      groups[dateKey].scans.push(scan);
    });
    
    // Sort groups by date (newest first)
    return Object.values(groups).sort((a, b) => b.date - a.date);
  }, [scans]);

  const toggleSection = (dateKey) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

  const formatDateHeader = (date) => {
    const fullDate = format(date, 'MMMM d, yyyy');
    if (isToday(date)) return `Today (${fullDate})`;
    if (isYesterday(date)) return `Yesterday (${fullDate})`;
    return fullDate;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="w-full px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NavigationMenu />
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="text-left">
                <h1 className="text-base font-bold leading-tight">FlipValue</h1>
                <p className="text-[10px] text-muted-foreground -mt-0.5">Search History</p>
              </div>
            </button>
          </div>
          <div className="flex items-center gap-2">
            {selectMode && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectAll}
                >
                  {selectedIds.size === scans.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={exitSelectMode}
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            )}
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Undo Banner */}
      {pendingDelete && (
        <div className="sticky top-[57px] z-20 bg-zinc-800 text-white px-4 py-2 flex items-center justify-between animate-slide-down">
          <span className="text-sm">Search deleted</span>
          <button
            onClick={undoDelete}
            className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <Undo2 className="w-4 h-4" />
            Undo
          </button>
        </div>
      )}

      {/* Multi-select Action Bar */}
      {selectMode && selectedIds.size > 0 && (
        <div className="sticky top-[57px] z-20 bg-destructive text-white px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium">
            {selectedIds.size} selected
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={deleteSelectedScans}
            className="gap-1.5"
          >
            <Trash2 className="w-4 h-4" />
            Delete Selected
          </Button>
        </div>
      )}

      <main className="w-full px-4 py-4 flex-1">
        {/* Guest mode notice */}
        {isGuest && scans.length > 0 && (
          <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <Lock className="w-3 h-3" />
              Showing last {GUEST_HISTORY_LIMIT} searches. 
              <button onClick={() => navigate('/auth?mode=signup')} className="text-primary hover:underline font-medium">
                Sign up
              </button> to save unlimited history.
            </p>
          </div>
        )}
        
        {/* Search count with Delete All and Select */}
        {scans.length > 0 && !selectMode && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              {isGuest ? `${scans.length} of ${GUEST_HISTORY_LIMIT} searches` : `${scans.length} search${scans.length !== 1 ? 'es' : ''} in history`}
            </p>
            {!isGuest && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectMode(true)}
                  className="gap-1.5 h-8"
                >
                  <CheckSquare className="w-4 h-4" />
                  <span className="hidden sm:inline">Select</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteAllDialog(true)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5 h-8"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete All
                </Button>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="py-4">
            <ListSkeleton count={6} />
          </div>
        ) : scans.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No search history yet</p>
            <p className="text-sm mb-4">Search for items to see them here</p>
            <Button onClick={() => navigate('/dashboard')} className="gap-2">
              <Search className="w-4 h-4" />
              Start Searching
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {groupedScans.map((group) => {
              const isCollapsed = collapsedSections.has(group.dateKey);
              
              return (
                <div key={group.dateKey}>
                  {/* Date Header - All groups are collapsible */}
                  <button
                    onClick={() => toggleSection(group.dateKey)}
                    className="w-full flex items-center gap-3 mb-2 cursor-pointer hover:opacity-80"
                  >
                    <span className="text-muted-foreground">
                      {isCollapsed ? (
                        <ChevronRight className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </span>
                    <h3 className="text-sm font-semibold text-muted-foreground">
                      {formatDateHeader(group.date)}
                    </h3>
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-xs text-muted-foreground">
                      {group.scans.length} search{group.scans.length !== 1 ? 'es' : ''}
                    </span>
                  </button>
                  
                  {/* Searches for this date - Collapsible */}
                  {!isCollapsed && (
                    <div className="space-y-2">
                      {group.scans.map(scan => (
                        <div
                          key={scan.id}
                          onClick={() => {
                            if (selectMode) {
                              toggleSelect(scan.id);
                            } else {
                              navigate(`/search/${scan.id}`);
                            }
                          }}
                          className={`bg-card rounded-xl border p-3 cursor-pointer transition-colors ${
                            selectMode && selectedIds.has(scan.id)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-muted-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {/* Checkbox for select mode */}
                            {selectMode && (
                              <Checkbox
                                checked={selectedIds.has(scan.id)}
                                onCheckedChange={() => toggleSelect(scan.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="flex-shrink-0"
                              />
                            )}
                            
                            {scan.comps?.[0]?.image_url ? (
                              <img
                                src={scan.comps[0].image_url}
                                alt={scan.item.model}
                                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                                <Package className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                            
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-sm truncate">
                                {scan.item.brand} {scan.item.model}
                              </h3>
                              <p className="text-xs text-muted-foreground truncate">
                                {scan.item.category} • {scan.item.condition}
                              </p>
                            </div>
                            
                            <div className="text-right flex-shrink-0 flex items-center gap-2">
                              <div>
                                <p className="font-bold text-sm text-primary font-mono">
                                  ${scan.average_price?.toFixed(2) || '0.00'}
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                  avg price
                                </p>
                              </div>
                              
                              {/* Individual Delete Button - Always visible */}
                              {!selectMode && (
                                <button
                                  onClick={(e) => deleteScan(scan.id, e)}
                                  className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              
                              {!selectMode && (
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Delete All Dialog */}
      <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete All Search History
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete all {scans.length} search{scans.length !== 1 ? 'es' : ''}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteAllDialog(false)}
              className="flex-1"
              disabled={deletingAll}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteAllScans}
              className="flex-1 gap-2"
              disabled={deletingAll}
            >
              {deletingAll ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Delete All
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
